/**
 * Catálogo de logisticas: para qué sirve cada una, alta de cuenta y pasos de API.
 * UI de Configuración → Envíos consume esto; no hardcodear copy en paneles.
 */
import type { ShippingProviderId } from "./domain-types";

/** Tipos de servicio que un tenant puede ofrecer / que un provider cubre. */
export type LogisticsServiceType =
  | "pickup"
  | "immediate" // envío inmediato / express on-demand
  | "same_day"
  | "next_day"
  | "scheduled"
  | "national_parcel"
  | "branch_pickup" // retiro en sucursal del carrier
  | "coordinate"; // a coordinar (WA / teléfono)

export type LogisticsCatalogEntry = {
  id: ShippingProviderId | "manual" | "coordinate";
  name: string;
  shortDescription: string;
  /** Servicios que este medio puede cubrir. */
  serviceTypes: LogisticsServiceType[];
  /** Alta de cuenta del cliente final (merchant). */
  signupUrl: string | null;
  docsUrl: string | null;
  /** Pasos para obtener credenciales e integrar. */
  setupSteps: string[];
  credentialFields: Array<{ key: string; label: string; secret?: boolean }>;
  featureFlag?: string;
  /** Notas operativas / limitaciones conocidas (p. ej. live API pendiente). */
  notes?: string[];
};

export const LOGISTICS_SERVICE_LABELS: Record<LogisticsServiceType, string> = {
  pickup: "Retiro en local",
  immediate: "Envío inmediato",
  same_day: "Envío el mismo día",
  next_day: "Envío al día siguiente",
  scheduled: "Envío programado",
  national_parcel: "Correo / mensajería nacional",
  branch_pickup: "Retiro en sucursal del carrier",
  coordinate: "A coordinar",
};

/** Copy corto para hub/tooltips — siempre en español. */
export const LOGISTICS_SERVICE_DESCRIPTIONS: Record<LogisticsServiceType, string> = {
  pickup: "El cliente retira el pedido en tu local o sucursal.",
  immediate: "Salís a entregar ahora con tu flota o un courier on-demand (minutos).",
  same_day: "El pedido se entrega hoy, más tarde en el día (no es express de minutos).",
  next_day: "El pedido se entrega mañana u otro día hábil siguiente.",
  scheduled: "El cliente elige día y franja horaria de entrega.",
  national_parcel: "Envíos a todo el país con correo o paquetería (Andreani, OCA, etc.).",
  branch_pickup: "El cliente retira en un punto o sucursal del carrier (Pickit, Correo, etc.).",
  coordinate: "Acordás la entrega por WhatsApp o teléfono, sin cotización automática.",
};

export const LOGISTICS_CATALOG: LogisticsCatalogEntry[] = [
  {
    id: "cabify",
    name: "Cabify Logistics",
    shortDescription: "Entrega on-demand, mismo día y día siguiente con flota Cabify.",
    serviceTypes: ["immediate", "same_day", "next_day"],
    signupUrl: "https://cabify.com/es/business",
    docsUrl: "https://developers.cabify.com/docs/introduction",
    setupSteps: [
      "Creá o iniciá sesión en Cabify Business / Cabify Logistics como administrador.",
      "Entrá a Apps → Cabify Logistics API → Conectar → Generar API Keys (UUID + Secret).",
      "Pegá el Client ID (UUID) y el Client Secret en esta pantalla.",
      "Completá la dirección de origen (pickup) con lat/lon o dirección completa.",
      "Probá la conexión: Structura pide un access token OAuth y lista shipping types disponibles.",
      "Sandbox: usá cabify-sandbox.com; producción: cabify.com.",
      "Esta integración no requiere configurar un webhook manual.",
    ],
    credentialFields: [
      { key: "clientId", label: "Client ID (UUID)" },
      { key: "clientSecret", label: "Client Secret", secret: true },
    ],
    featureFlag: "cabify_shipping",
  },
  {
    id: "uber_direct",
    name: "Uber Direct",
    shortDescription: "Courier on-demand de Uber para pickup en tu local y entrega al cliente.",
    serviceTypes: ["immediate", "same_day", "scheduled"],
    signupUrl: "https://direct.uber.com/",
    docsUrl: "https://developer.uber.com/docs/deliveries/overview",
    setupSteps: [
      "Creá una cuenta Uber Direct y entrá al Dashboard (modo Test = sandbox).",
      "En Developer copiá Customer ID, Client ID (Application ID) y Client Secret.",
      "Pegá las tres credenciales acá y completá la dirección de pickup del local.",
      "Probá la conexión: Structura pide un OAuth token con scope eats.deliveries.",
      "Sandbox no genera couriers reales. Producción requiere aprobación Uber + billing.",
      "Docs: https://developer.uber.com/docs/deliveries/get-started",
    ],
    credentialFields: [
      { key: "customerId", label: "Customer ID" },
      { key: "clientId", label: "Client ID (Application ID)" },
      { key: "clientSecret", label: "Client Secret", secret: true },
    ],
    featureFlag: "uber_direct_shipping",
  },
  {
    id: "pedidosya",
    name: "PedidosYa Delivery",
    shortDescription: "Courier on-demand vía Partner POS (mock Fase 1)",
    serviceTypes: ["immediate", "same_day"],
    signupUrl: "https://partner.pedidosya.com/",
    docsUrl: "https://developer.pedidosya.com/",
    setupSteps: [
      "Activá el provider en modo Sandbox + Mock",
      "Completá Vendor / Chain / Integration Code cuando el Partner Portal te los dé",
      "Registrá la Webhook URL de Structura en Partner Portal",
      "Test Connection (mock) y luego homologación con sandbox oficial",
    ],
    credentialFields: [
      { key: "vendorCode", label: "Vendor Code", secret: false },
      { key: "chainCode", label: "Chain Code", secret: false },
      { key: "integrationCode", label: "Integration Code", secret: false },
      { key: "username", label: "Username", secret: false },
      { key: "password", label: "Password", secret: true },
      { key: "apiToken", label: "API Token", secret: true },
      { key: "webhookSecret", label: "Webhook Secret", secret: true },
    ],
    notes: [
      "Fase 1: Driver Mock sin red. No llama a producción.",
      "Base URL típica: partners-pedidosya.deliveryhero.io",
    ],
    featureFlag: "delivery_provider_pedidosya",
  },
  {
    id: "rapiboy",
    name: "Rapiboy",
    shortDescription: "Courier last-mile Rapiboy (mock Fase 1). Independiente de Rappi / Rappi Envíos.",
    serviceTypes: ["immediate", "same_day"],
    signupUrl: "https://rapiboy.com/",
    docsUrl: "https://www.postman.com/rapiboy/rapiboy-s-public-workspace/collection/xk3b5nv/rapiboy-api",
    setupSteps: [
      "Activá Rapiboy en Sandbox + Mock",
      "Completá Merchant ID + API Key cuando Rapiboy te las dé",
      "Registrá la Webhook URL de Structura",
      "Test Connection (mock) y luego sandbox oficial (Postman collection)",
    ],
    credentialFields: [
      { key: "merchantId", label: "Merchant ID", secret: false },
      { key: "branchId", label: "Branch ID", secret: false },
      { key: "apiKey", label: "API Key", secret: true },
      { key: "webhookSecret", label: "Webhook Secret", secret: true },
    ],
    notes: [
      "Rapiboy ≠ Rappi Envíos. Son productos y empresas distintas.",
      "Fase 1: Driver Mock sin red.",
      "API ref: Postman Rapiboy public workspace",
    ],
    featureFlag: "delivery_provider_rapiboy",
  },


  {
    id: "zipnova",
    name: "Zipnova Envíos",
    shortDescription: "Cotizá y despachá con múltiples carriers vía Zipnova (ex Zippin).",
    serviceTypes: ["national_parcel", "branch_pickup", "same_day"],
    signupUrl: "https://www.zipnova.com/",
    docsUrl: "https://docs.zipnova.com/envios/",
    setupSteps: [
      "Creá una cuenta en Zipnova Envíos.",
      "Andá a Configuración → Integraciones → Gestionar credenciales y webhooks.",
      "Creá credenciales API v2 (Key + Secret) y anotá el account_id.",
      "Pegá Key, Secret y Account ID acá. Opcional: Origin ID del address book.",
      "Probá la conexión con una cotización al CP de prueba.",
      "Copiá la URL de webhook de esta pantalla (incluye tu slug) y creá 2 webhooks en Zipnova: topics status y shipment.",
    ],
    credentialFields: [
      { key: "apiKey", label: "API Key" },
      { key: "apiSecret", label: "API Secret", secret: true },
      { key: "accountId", label: "Account ID" },
    ],
    featureFlag: "zipnova_shipping",
  },

  {
    id: "pickit",
    name: "Pickit",
    shortDescription: "Puntos Pickit y envíos a domicilio (LATAM).",
    serviceTypes: ["branch_pickup", "national_parcel", "same_day"],
    signupUrl: "https://www.pickit.com.ar/",
    docsUrl: "https://developers.pickit.net/api/introduccion",
    setupSteps: [
      "Creá o usá tu cuenta retailer en Pickit.",
      "Obtené apiKey y token (tokenId) desde el panel / onboarding Pickit.",
      "Pegá apiKey y token acá. Opcional: dirección de origen del local.",
      "Activá sandbox (UAT) para pruebas o producción (api.pickit.net).",
      "Probá la conexión (lista de puntos) y activá el proveedor.",
    ],
    credentialFields: [
      { key: "apiKey", label: "API Key" },
      { key: "apiToken", label: "Token (tokenId)", secret: true },
    ],
    featureFlag: "pickit_shipping",
  },

  {
    id: "shipnow",
    name: "Shipnow",
    shortDescription: "Cotizá y despachá pedidos con tu cuenta Shipnow.",
    serviceTypes: ["national_parcel", "branch_pickup"],
    signupUrl: "https://www.shipnow.com.ar/",
    docsUrl: "https://developers.shipnow.com.ar/",
    setupSteps: [
      "Creá tu cuenta Shipnow.",
      "Generá un API token en el panel Shipnow.",
      "Pegá el token acá y elegí sandbox o producción.",
      "Configurá peso default y modo Proveedor / Híbrido / Manual.",
      "Esta integración no requiere configurar un webhook manual.",
    ],
    credentialFields: [{ key: "apiToken", label: "API Token", secret: true }],
    featureFlag: "shipping_shipnow",
  },
  {
    id: "treggo",
    name: "Treggo",
    shortDescription: "Creá y rastreá envíos con Treggo.",
    serviceTypes: ["national_parcel", "same_day"],
    signupUrl: "https://treggo.co/",
    docsUrl: "https://docs.treggo.co/",
    setupSteps: [
      "Creá tu cuenta Treggo.",
      "En Mi perfil obtené email + API Secret.",
      "Cargá origen (dirección de pickup) y probá la conexión.",
      "Al guardar, Structura registra el webhook con tu slug. También podés copiar la URL de esta pantalla.",
    ],
    credentialFields: [
      { key: "email", label: "Email Treggo" },
      { key: "apiSecret", label: "API Secret", secret: true },
    ],
    featureFlag: "treggo_shipping",
  },
  {
    id: "correo_argentino",
    name: "Correo Argentino",
    shortDescription: "Cotizá e importá envíos vía API MiCorreo (domicilio / sucursal).",
    serviceTypes: ["national_parcel", "branch_pickup"],
    signupUrl: "https://www.correoargentino.com.ar/MiCorreo/",
    docsUrl: "https://www.correoargentino.com.ar/MiCorreo/public/img/pag/apiMiCorreo.pdf",
    setupSteps: [
      "Pedí a Correo las credenciales API (Basic Auth) para QA y/o producción.",
      "Obtené el JWT con POST /token y validá tu usuario MiCorreo (/users/validate) para el customerId.",
      "Completá customerId, CP de origen y datos del remitente. Probá con Sandbox/Mock.",
      "Cuando esté listo: «Pasar a producción» (mock off + environment production + test live).",
      "QA: https://apitest.correoargentino.com.ar/micorreo/v1",
    ],
    credentialFields: [
      { key: "apiUser", label: "Usuario API (Basic Auth)" },
      { key: "password", label: "Password API", secret: true },
      { key: "customerId", label: "customerId MiCorreo" },
      { key: "email", label: "Email MiCorreo (opcional)" },
    ],
    featureFlag: "correo_argentino_shipping",
  },
  {
    id: "oca",
    name: "OCA ePak",
    shortDescription: "Cotizá, creá y etiquetá envíos OCA (puerta / sucursal) vía ePak.",
    serviceTypes: ["national_parcel", "branch_pickup"],
    signupUrl: "https://ocaepak.oca.com.ar/",
    docsUrl: "https://developers.oca.com.ar/epak.html",
    setupSteps: [
      "Creá usuario e-Pak en ocaepak.oca.com.ar (o usá las credenciales de prueba oficiales).",
      "Completá CUIT, N° de cuenta, operativas y centro de costo.",
      "Sandbox QA: integraciones.ocadev.com.ar — datos de prueba en la doc OCA Developers.",
      "Activá mockMode para probar sin red; desactivá para pegarle al ASMX real.",
    ],
    credentialFields: [
      { key: "usr", label: "Usuario e-Pak" },
      { key: "password", label: "Password e-Pak", secret: true },
      { key: "cuit", label: "CUIT (##-########-#)" },
      { key: "nroCuenta", label: "N° de cuenta OCA" },
    ],
    featureFlag: "oca_epak_shipping",
  },
  {
    id: "andreani",
    name: "Andreani",
    shortDescription: "Cotizá, creá y rastreá envíos Andreani (domicilio / sucursal).",
    serviceTypes: ["national_parcel", "branch_pickup"],
    signupUrl: "https://www.andreani.com/",
    docsUrl: "https://developers.andreani.com/",
    setupSteps: [
      "Solicitá acceso API en tu cuenta Andreani.",
      "Completá usuario, contraseña API, código de cliente y contratos.",
      "Activá sandbox (api.qa) para pruebas y luego producción.",
      "Esta integración no requiere configurar un webhook manual.",
    ],
    credentialFields: [
      { key: "user", label: "Usuario API" },
      { key: "password", label: "Contraseña API", secret: true },
      { key: "clientCode", label: "Código de cliente" },
    ],
    featureFlag: "andreani_shipping",
  },
  {
    id: "manual",
    name: "Tarifas manuales",
    shortDescription: "Precios fijos por zona (CABA / GBA / Interior) sin carrier.",
    serviceTypes: ["national_parcel", "scheduled"],
    signupUrl: null,
    docsUrl: null,
    setupSteps: [
      "Definí precios por zona en Configuración → Envíos.",
      "Usá modo Manual (solo tarifas) o Híbrido (fallback si falla el provider).",
    ],
    credentialFields: [],
  },
  {
    id: "coordinate",
    name: "A coordinar",
    shortDescription: "El cliente elige coordinar el envío por WhatsApp o teléfono; sin cotización automática.",
    serviceTypes: ["coordinate"],
    signupUrl: null,
    docsUrl: null,
    setupSteps: [
      "Activá “A coordinar” en Configuración → Envíos.",
      "Opcional: precio fijo o $0 y texto de contacto (WhatsApp / teléfono).",
      "En checkout aparece como opción de entrega; el pedido queda pendiente de coordinación.",
    ],
    credentialFields: [],
  },

  {
    id: "enviopack",
    name: "Enviopack",
    shortDescription: "Agregador multi-carrier (correo nacional AR)",
    serviceTypes: ["national_parcel", "branch_pickup", "next_day"],
    signupUrl: "https://enviopack.com/",
    docsUrl: "https://developers.enviopack.com.ar/",
    setupSteps: [
      "Crear cuenta Enviopack",
      "Obtener api-key y secret",
      "Pegar credenciales en Logística → Enviopack",
      "Probar cotización (mock o live)",
      "A diferencia de otras integraciones, la URL de notificaciones NO se manda por request: configurala una sola vez en Enviopack → Configuración → Notificaciones con la URL de esta pantalla (incluye tu slug).",
    ],
    credentialFields: [
      { key: "api_key", label: "API Key", secret: true },
      { key: "secret", label: "Secret", secret: true },
    ],
    featureFlag: "enviopack_shipping",
  },
];

export function getLogisticsCatalogEntry(
  id: string,
): LogisticsCatalogEntry | undefined {
  return LOGISTICS_CATALOG.find((e) => e.id === id);
}
